import { useEffect, useRef, useState } from 'react'
import { useMedia } from 'react-use'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import { useLenis } from 'lenis/react'
import Link from 'next/link'

import Button from '@/components/base/button'
import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Motion from '@/components/base/motion'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import Footer from '@/components/layout/footer'
import { collectionLandingDesktopRevealAnimation } from '@/components/sections/animations/collection-landing-desktop'
import Spacer from '@/components/sections/spacer'
import { useStore } from '@/contexts/store'
import useStableViewport from '@/hooks/use-stable-viewport'
import { capitalizeLetters } from '@/utils/strings'

import { cn } from '@/lib/utils'
import { WPPageData } from '@/types'

gsap.registerPlugin(ScrollTrigger)

export type CollectionChildrenProps = Partial<{
  pageProps: WPPageData & {
    object?: {
      name: string
      description: string
    }
    children?: {
      title: string
      link: string
    }[]
    collection?: any[]
  }
}>

const CollectionChildren = ({ pageProps }: CollectionChildrenProps) => {
  const isSmall = useMedia('(max-width: 767px)', false)
  const rootRef = useRef<HTMLDivElement>(null)
  const mobileRef = useRef<HTMLDivElement>(null)
  const stickyRef = useRef<HTMLDivElement>(null)
  const { strings } = useStore()
  const lenis = useLenis()
  const [stickyTop, setStickyTop] = useState<number | null>(null)
  const { height: viewportHeight } = useStableViewport()
  const normalizeTaxonomy = (value?: string) =>
    (value || '').trim().toLowerCase()

  useEffect(() => {
    document.documentElement.classList.remove('dark')
    document.documentElement.style.setProperty('--page-background', '#eee')
  }, [])

  useEffect(() => {
    if (isSmall) return

    const node = stickyRef.current
    if (!node) return

    const rafId = window.requestAnimationFrame(() => {
      setStickyTop(node.getBoundingClientRect().top)
    })

    return () => {
      window.cancelAnimationFrame(rafId)
    }
  }, [isSmall])

  useGSAP(
    () => {
      if (isSmall || !lenis) return

      const root = rootRef.current
      if (!root) return

      const itemNodes = Array.from(
        root.querySelectorAll('[data-collection-item]')
      ) as HTMLElement[]
      if (itemNodes.length === 0) return

      const childNodes = Array.from(
        root.querySelectorAll('[data-collection-child]')
      ) as HTMLElement[]

      const itemOpacitySetters = itemNodes.map((node) =>
        gsap.quickTo(node, 'opacity', { duration: 0.2, ease: 'power2.out' })
      )
      const childOpacitySetters = childNodes.map((node) =>
        gsap.quickTo(node, 'opacity', { duration: 0.2, ease: 'power2.out' })
      )

      const updateState = () => {
        const viewportCenter = viewportHeight * 0.5
        let activeTaxonomy = ''
        let minDistance = Number.POSITIVE_INFINITY

        itemNodes.forEach((node, index) => {
          const rect = node.getBoundingClientRect()

          const center = rect.top + rect.height * 0.5
          const distance = Math.abs(center - viewportCenter)
          const maxDistance = Math.max(viewportCenter, 1)
          const normalized = gsap.utils.clamp(0, 1, distance / maxDistance)
          const opacity = 1 - normalized * 0.9

          itemOpacitySetters[index](opacity)

          if (distance < minDistance) {
            minDistance = distance
            activeTaxonomy = normalizeTaxonomy(node.dataset.collectionTaxonomy)
          }
        })

        childNodes.forEach((node, index) => {
          const childTaxonomy = normalizeTaxonomy(
            node.dataset.collectionChildTaxonomy
          )
          const isActive = childTaxonomy && childTaxonomy === activeTaxonomy
          childOpacitySetters[index](isActive ? 1 : 0.2)
        })
      }

      lenis.on('scroll', updateState)

      // Call updateState continuously for 1 second on mount so that initial
      // opacities are set correctly before the user scrolls
      const startTime = performance.now()
      let rafId: number
      const runInitial = () => {
        updateState()
        if (performance.now() - startTime < 1000) {
          rafId = requestAnimationFrame(runInitial)
        }
      }
      // eslint-disable-next-line no-unused-vars
      rafId = requestAnimationFrame(runInitial)

      const hoverHandlers: Array<{ node: HTMLElement; handler: () => void }> =
        []

      childNodes.forEach((childNode) => {
        const taxonomy = normalizeTaxonomy(
          childNode.dataset.collectionChildTaxonomy
        )
        if (!taxonomy) return

        const handler = () => {
          const targetNode = itemNodes.find(
            (itemNode) =>
              normalizeTaxonomy(itemNode.dataset.collectionTaxonomy) ===
              taxonomy
          )
          if (!targetNode) return

          const targetRect = targetNode.getBoundingClientRect()
          const target =
            window.scrollY +
            targetRect.top +
            targetRect.height * 0.5 -
            viewportHeight * 0.5

          if (lenis) {
            lenis.scrollTo(target, { duration: 1 })
          } else {
            window.scrollTo({ top: target, behavior: 'smooth' })
          }
        }

        childNode.addEventListener('pointerenter', handler)
        hoverHandlers.push({ node: childNode, handler })
      })

      return () => {
        hoverHandlers.forEach(({ node, handler }) => {
          node.removeEventListener('pointerenter', handler)
        })
        lenis.off('scroll', updateState)
      }
    },
    {
      scope: rootRef,
      dependencies: [
        isSmall,
        lenis,
        viewportHeight,
        pageProps?.collection,
        pageProps?.children
      ],
      revertOnUpdate: true
    }
  )

  // Mobile: GSAP fadeIn
  const mobileMotionRef = useRef<HTMLDivElement>(null)

  useGSAP(
    () => {
      if (!isSmall || !mobileMotionRef.current) return

      gsap.fromTo(
        mobileMotionRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: mobileMotionRef.current,
            once: true,
            start: 'top 82%',
            invalidateOnRefresh: true,
            toggleActions: 'play none none none'
          }
        }
      )
    },
    {
      scope: mobileMotionRef,
      dependencies: [isSmall, pageProps?.collection],
      revertOnUpdate: true
    }
  )

  const renderDesktop = () => {
    return (
      <Motion animation={collectionLandingDesktopRevealAnimation}>
        {(motionRef) => (
          <div ref={motionRef}>
            <Container ref={rootRef}>
              <Grid>
                <div className="space-y-spacing-lg md:col-span-4 lg:col-span-7">
                  {pageProps?.collection?.map((item, index) => {
                    const title = capitalizeLetters(item.title)
                    return (
                      <Grid
                        className="items-end gap-x-gutter md:grid-cols-4 lg:grid-cols-7"
                        key={index}
                        data-collection-item
                        data-collection-taxonomy={item.lowest_taxonomy}
                      >
                        <div className="md:col-span-2 lg:col-span-2">
                          <Link
                            href={item.link}
                            className="flex"
                            scroll={false}
                          >
                            <Typography
                              variant="h3"
                              className="xl:text-h4 lg:text-h4"
                            >
                              {title}
                            </Typography>
                          </Link>
                        </div>
                        <div className="md:col-span-2 lg:col-span-5">
                          <div className="w-full">
                            <Link
                              href={item.link}
                              scroll={false}
                              aria-hidden="true"
                              tabIndex={-1}
                            >
                              <div className="aspect-video w-full">
                                <img
                                  src={item.image?.url}
                                  alt=""
                                  className="h-auto w-full max-w-none object-cover"
                                />
                              </div>
                            </Link>
                          </div>
                        </div>
                      </Grid>
                    )
                  })}
                </div>
                <div
                  ref={stickyRef}
                  className="sticky flex h-fit flex-col gap-spacing-sm self-start md:col-span-2 md:col-start-5 lg:col-span-4 lg:col-start-9"
                  style={{
                    top: stickyTop !== null ? `${stickyTop}px` : undefined
                  }}
                >
                  <div
                    className="flex items-start justify-between"
                    data-motion-title
                  >
                    <Typography
                      variant="h2"
                      component="h1"
                      className="lg:text-h2 md:text-h3"
                    >
                      {capitalizeLetters(pageProps?.object?.name || '')}
                    </Typography>
                    <div className="flex rounded-radius-minimal bg-surface-primary-light p-spacing-2xs">
                      <Typography
                        variant="small"
                        className="condensed text-text-heading-invert-light"
                      >
                        {pageProps?.collection?.length
                          .toString()
                          .padStart(2, '0') || 0}
                      </Typography>
                    </div>
                  </div>
                  <div className="flex" data-motion-text>
                    <Typography
                      variant="regular"
                      className="text-text-primary-light"
                    >
                      {pageProps?.object?.description}
                    </Typography>
                  </div>
                  {pageProps?.children?.length ? (
                    <div
                      className="mt-spacing-md flex flex-col gap-y-spacing-xs"
                      data-motion-item
                    >
                      {pageProps.children.map((child, index) => (
                        <button
                          key={index}
                          className="flex cursor-pointer text-left"
                          data-collection-child
                          data-collection-child-taxonomy={child.title}
                          aria-label={`${strings?.labels?.subcollections || 'Sotto-collezioni'}: ${capitalizeLetters(child.title)}`}
                        >
                          <Typography
                            variant="h4"
                            className="lg:text-h4 md:text-h5"
                          >
                            {capitalizeLetters(child.title)}
                          </Typography>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </Grid>
            </Container>
          </div>
        )}
      </Motion>
    )
  }

  const onMobileChildClick = (taxonomyTitle: string) => {
    const root = mobileRef.current
    if (!root) return

    const taxonomy = normalizeTaxonomy(taxonomyTitle)
    if (!taxonomy) return

    const itemNodes = Array.from(
      root.querySelectorAll('[data-collection-taxonomy]')
    ) as HTMLElement[]
    const targetNode = itemNodes.find(
      (itemNode) =>
        normalizeTaxonomy(itemNode.dataset.collectionTaxonomy) === taxonomy
    )

    if (!targetNode) return

    const rect = targetNode.getBoundingClientRect()
    const target = window.scrollY + rect.top - 12

    if (lenis) {
      lenis.scrollTo(target, { duration: 1 })
    } else {
      window.scrollTo({ top: target, behavior: 'smooth' })
    }
  }

  const renderMobile = () => {
    return (
      <Container ref={mobileRef}>
        <div ref={mobileMotionRef} data-motion-gallery className="opacity-0">
          <div className="flex items-start justify-between border-b-[2px] border-b-[rgba(0,0,0,.2)] pb-spacing-xs">
            <div className="flex gap-spacing-xs">
              <Link
                href={strings?.labels?.collections_link?.url || '/collections'}
                className="flex"
                aria-label={strings?.labels?.back || 'Torna indietro'}
              >
                <SVGIcon
                  symbol="cta-arrow"
                  className="size-[24px] scale-x-[-1]"
                />
              </Link>
              <Typography variant="h4" component="h1">
                {capitalizeLetters(pageProps?.object?.name || '')}
              </Typography>
            </div>
            <div className="flex rounded-radius-minimal bg-surface-primary-light p-spacing-2xs">
              <Typography
                variant="small"
                className="condensed text-text-heading-invert-light"
              >
                {pageProps?.collection?.length.toString().padStart(2, '0') || 0}
              </Typography>
            </div>
          </div>
          {pageProps?.children?.length ? (
            <div
              className={cn(
                'transition-top max-w-screen sticky top-[48px] z-10 mt-spacing-md flex gap-spacing-2xs overflow-x-scroll bg-[#eeeeee]/70 py-spacing-xs backdrop-blur-md duration-300 ease-in-out-cubic [.header-hidden_&]:top-[0px]'
              )}
            >
              {pageProps.children.map((child, index) => (
                <Button
                  key={index}
                  className="flex cursor-pointer whitespace-nowrap"
                  data-collection-child
                  data-collection-child-taxonomy={child.title}
                  variant="secondary"
                  onClick={() => onMobileChildClick(child.title)}
                  aria-label={`${strings?.labels?.subcollections || 'Sotto-collezioni'}: ${capitalizeLetters(child.title)}`}
                >
                  <Typography variant="small">
                    {capitalizeLetters(child.title)}
                  </Typography>
                </Button>
              ))}
            </div>
          ) : null}
          <Spacer variant="lg" />
          <div className="flex flex-col gap-y-spacing-md">
            {pageProps?.collection?.map((item, index) => {
              const title = capitalizeLetters(item.title)
              return (
                <Link
                  href={item.link}
                  key={index}
                  className="flex flex-col gap-spacing-xs"
                  scroll={false}
                  data-collection-taxonomy={item.lowest_taxonomy}
                >
                  <div className="aspect-video w-full">
                    <img
                      src={item.image?.url}
                      alt=""
                      className="h-auto w-full max-w-none object-cover"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Typography variant="h4">{title}</Typography>
                    <span className="flex items-center justify-center rounded-radius-minimal border border-border-secondary-light px-spacing-xs py-spacing-xs dark:border-border-secondary-dark">
                      <Typography variant="small">
                        {item.lowest_taxonomy}
                      </Typography>
                    </span>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      </Container>
    )
  }

  return (
    <>
      <Spacer variant="xxl" />
      {isSmall ? renderMobile() : renderDesktop()}
      <Spacer variant="xxl" />
      <Spacer variant="md" />
      <Footer footer={pageProps?.acf?.footer} />
    </>
  )
}

export default CollectionChildren
